/**
 * MfaModal — dialog step-up MFA a 2 step (PR 5.5).
 *
 * Flussi supportati:
 *
 * 1) STEP-UP POST-LOGIN (resolver prop = null/undefined).
 *    L'utente e' gia' loggato ma `auth_time` e' stale (> 5 min) e
 *    sta per eseguire un'azione Tier 2. Per refreshare davvero
 *    `auth_time` (immutabile lato Firebase) serve una REAUTH reale:
 *
 *    Step 1: password   -> `reauthenticateWithCredential(currentUser,
 *                          EmailAuthProvider.credential(email, password))`.
 *      - Successo "lineare":   avanza a step 2 con resolver = null.
 *      - Errore `auth/multi-factor-auth-required`: estrai resolver,
 *        salvalo in stato, avanza a step 2 con quel resolver.
 *      - Altri errori (wrong-password, too-many-requests, ...): errore
 *        inline, NO avanzamento, NO chiusura modal, NO log password.
 *
 *    Step 2: OTP TOTP    -> se ho resolver:
 *                            `TotpMultiFactorGenerator.assertionForSignIn`
 *                            + `resolver.resolveSignIn`.
 *                          se NON ho resolver (account senza MFA o
 *                            emulator con `_mfaDevBypass`):
 *                            verifica solo regex 6 cifre.
 *      Su success: `useAuth().refreshTokenAndAssert()` per propagare
 *      il nuovo `auth_time` nel singleton, poi `onSuccess()`.
 *
 * 2) LOGIN MFA CHALLENGE (resolver prop != null).
 *    LoginPage ha gia' ricevuto `auth/multi-factor-auth-required` da
 *    `signInWithEmailAndPassword`. La password e' appena stata
 *    digitata e validata, quindi salta direttamente a step 2 OTP.
 *    Su success il login viene completato dal resolver e
 *    `onAuthStateChanged` aggiorna il singleton; NO refresh manuale.
 *
 * Sicurezza:
 *  - Password input type="password" + autoComplete="current-password".
 *  - OTP input inputMode="numeric" + autoComplete="one-time-code".
 *  - La password vive in state solo durante il submit; viene azzerata
 *    appena lo step avanza e ad ogni riapertura del modal. MAI loggata.
 *  - Errori al cliente sempre generici/specifici per il caso UX,
 *    senza leak di dettagli del backend.
 *
 * Vedi docs/known-issues.md sezione "Risolti — PR 5.5" per il bug
 * BUG-2026-05-15 risolto da questo refactor.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  type MultiFactorError,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from '../../security/firebase';
import { useAuth } from '../../hooks/useAuth';

export interface MfaModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
  tier: 'tier1' | 'tier2';
  actionLabel: string;
  /**
   * Resolver opzionale: presente quando il modal e' aperto come risposta a
   * `auth/multi-factor-auth-required` durante il login. In step-up
   * post-login resta null/undefined: l'utente e' gia' autenticato e
   * deve fare prima password reauth, poi (eventualmente) OTP.
   */
  resolver?: MultiFactorResolver | null;
}

type Step = 'password' | 'otp';

const OTP_REGEX = /^\d{6}$/;

export function MfaModal({
  isOpen,
  onSuccess,
  onCancel,
  tier,
  actionLabel,
  resolver: initialResolver,
}: MfaModalProps) {
  // Catturato al mount: se inizio con resolver != null sono in login flow,
  // niente step 1, niente refreshTokenAndAssert al termine.
  const isLoginFlow = !!initialResolver;

  const [step, setStep] = useState<Step>(isLoginFlow ? 'otp' : 'password');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [internalResolver, setInternalResolver] =
    useState<MultiFactorResolver | null>(initialResolver ?? null);

  const passwordRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  const { refreshTokenAndAssert } = useAuth();

  // Reset stato form alla riapertura del modal. Resetta anche password
  // (mai trattenuta tra una sessione modal e la successiva).
  useEffect(() => {
    if (isOpen) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setStep(isLoginFlow ? 'otp' : 'password');
      setPassword('');
      setOtp('');
      setError(null);
      setSubmitting(false);
      setInternalResolver(initialResolver ?? null);
      /* eslint-enable react-hooks/set-state-in-effect */
    } else {
      // Sicurezza: azzera anche alla chiusura per non lasciare la
      // password in memoria piu' del necessario.
      setPassword('');
    }
  }, [isOpen, isLoginFlow, initialResolver]);

  const activeResolver = internalResolver;

  const headerCopy = useMemo(() => {
    if (step === 'password') {
      return {
        title: 'Conferma identita',
        body: `Stai per: ${actionLabel}. Per continuare inserisci la tua password.`,
      };
    }
    return {
      title: 'Verifica identita',
      body:
        `Stai per: ${actionLabel}. ` +
        (tier === 'tier2'
          ? "Inserisci il codice a 6 cifre dell'app di autenticazione per confermare l'azione critica."
          : "Inserisci il codice a 6 cifre dell'app di autenticazione."),
    };
  }, [step, actionLabel, tier]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length === 0) {
      setError('Inserisci la password.');
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      setError('Sessione non valida. Effettua nuovamente il login.');
      return;
    }
    setSubmitting(true);
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        password
      );
      await reauthenticateWithCredential(currentUser, credential);
      // Reauth riuscita senza MFA challenge (account senza secondo
      // fattore, oppure emulator con bypass). Avanza a step OTP.
      // Se l'account NON ha MFA enrolled lo step OTP funzionera' come
      // noop (regex 6 cifre); il rischio sicurezza e' coperto dalle
      // rules server-side che leggono `auth_time` dal token.
      setPassword('');
      setStep('otp');
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        try {
          const mfaResolver = getMultiFactorResolver(
            auth,
            err as MultiFactorError
          );
          setInternalResolver(mfaResolver);
          setPassword('');
          setStep('otp');
        } catch {
          setError(
            'Impossibile avviare la verifica a due fattori. Riprova.'
          );
        }
      } else if (
        code === 'auth/wrong-password' ||
        code === 'auth/invalid-credential' ||
        code === 'auth/invalid-login-credentials'
      ) {
        setError('Password non corretta.');
      } else if (code === 'auth/too-many-requests') {
        setError('Troppi tentativi. Riprova tra qualche minuto.');
      } else if (code === 'auth/user-disabled') {
        setError("Account disabilitato. Contatta l'amministratore.");
      } else if (code === 'auth/network-request-failed') {
        setError('Errore di rete. Controlla la connessione.');
      } else {
        setError('Verifica non riuscita. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!OTP_REGEX.test(otp)) {
      setError('Inserisci tutte e 6 le cifre.');
      return;
    }
    setSubmitting(true);
    try {
      if (activeResolver) {
        const hint = activeResolver.hints.find((h) => h.factorId === 'totp');
        if (!hint) {
          throw new Error('totp-not-enrolled');
        }
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(
          hint.uid,
          otp
        );
        await activeResolver.resolveSignIn(assertion);
      } else {
        // No resolver: o e' un account senza MFA (raro in prod), o siamo
        // su emulator con `_mfaDevBypass`. La reauth password e' gia'
        // andata a buon fine sopra, quindi `auth_time` e' fresco lato
        // server. Qui validiamo solo il formato dell'OTP per coerenza
        // UX.
        if (!auth.currentUser) {
          throw new Error('not-authenticated');
        }
      }

      // Step-up flow: propaga il nuovo auth_time nel singleton useAuth
      // cosi' la pill MFA + i gate Tier 2 si aggiornano subito.
      // Login flow: skip — onAuthStateChanged completera' la propagazione.
      if (!isLoginFlow) {
        try {
          await refreshTokenAndAssert();
        } catch {
          // Race difensiva: se per qualche motivo il singleton non ha
          // ancora visto `auth.currentUser`, NON bloccare il flusso.
          // Il prossimo `onIdTokenChanged` lo aggiornera' comunque.
        }
      }
      onSuccess();
    } catch (err) {
      const code = (err as { code?: string }).code;
      const msg = (err as Error).message ?? '';
      if (code === 'auth/invalid-verification-code') {
        setError('Codice non corretto. Riprova.');
      } else if (code === 'auth/code-expired') {
        setError("Codice scaduto. Genera un nuovo codice dall'app.");
      } else if (code === 'auth/totp-challenge-timeout') {
        setError("Codice scaduto. Genera un nuovo codice dall'app.");
      } else if (code === 'auth/too-many-requests') {
        setError('Troppi tentativi. Riprova tra qualche minuto.');
      } else if (msg === 'totp-not-enrolled') {
        setError(
          'Nessun secondo fattore TOTP configurato per questo account.'
        );
      } else if (msg === 'not-authenticated') {
        setError('Sessione non valida. Effettua nuovamente il login.');
      } else {
        setError('Verifica non riuscita. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          aria-describedby="mfa-modal-desc"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl shadow-xl p-6 md:p-8 focus:outline-none"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (step === 'password') {
              passwordRef.current?.focus();
            } else {
              otpRef.current?.focus();
            }
          }}
        >
          <Dialog.Title className="font-headline-md text-headline-md text-text-main mb-2">
            {headerCopy.title}
          </Dialog.Title>
          <Dialog.Description
            id="mfa-modal-desc"
            className="font-body-md text-text-secondary mb-6"
          >
            {headerCopy.body}
          </Dialog.Description>

          {step === 'password' ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <label
                htmlFor="mfa-password"
                className="block font-label-sm text-on-surface-variant"
              >
                Password
              </label>
              <input
                ref={passwordRef}
                id="mfa-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={error ? 'true' : 'false'}
                className="w-full h-12 px-4 rounded-lg border border-border-gray focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft font-body-md text-text-main"
              />
              {error && (
                <p role="alert" className="text-body-sm text-error">
                  {error}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 h-12 rounded-lg border border-border-gray font-body-md font-semibold text-on-surface hover:bg-surface-container-low transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={submitting || password.length === 0}
                  className="flex-1 h-12 rounded-lg bg-primary text-on-primary font-body-md font-bold hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Verifica…' : 'Continua'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <label
                htmlFor="mfa-otp"
                className="block font-label-sm text-on-surface-variant"
              >
                Codice OTP
              </label>
              <input
                ref={otpRef}
                id="mfa-otp"
                name="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                aria-invalid={error ? 'true' : 'false'}
                className="w-full h-14 px-4 text-center tracking-[0.5em] text-headline-md font-headline-md rounded-lg border border-border-gray focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft"
              />
              {error && (
                <p role="alert" className="text-body-sm text-error">
                  {error}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 h-12 rounded-lg border border-border-gray font-body-md font-semibold text-on-surface hover:bg-surface-container-low transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={submitting || otp.length !== 6}
                  className="flex-1 h-12 rounded-lg bg-primary text-on-primary font-body-md font-bold hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Verifica…' : 'Conferma'}
                </button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default MfaModal;
